import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2, ChevronRight, Maximize2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChatBubble = ({ token, businessId }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput("");
    setMessages(prev => [...prev, { role: "user", content: text }]);
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/assistant/chat`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId, business_id: businessId }),
      });
      const data = await res.json();
      if (res.ok) {
        setSessionId(data.session_id);
        setMessages(prev => [...prev, { role: "assistant", content: data.response, actions: data.actions }]);
      } else {
        setMessages(prev => [...prev, { role: "assistant", content: `Error: ${data.detail || "Something went wrong"}` }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Connection failed. Please try again." }]);
    }
    setLoading(false);
  };

  const executeAction = async (action) => {
    if (action.type === "navigate") {
      const pageMap = { dashboard: "/dashboard", bookkeeping: "/bookkeeping", "tax-calculator": "/tax-calculator", deductions: "/deductions", "cash-flow": "/cash-flow", "profit-loss": "/profit-loss", allocations: "/allocations", assistant: "/assistant" };
      navigate(pageMap[action.page] || "/dashboard");
      setOpen(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/assistant/execute-action`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action, business_id: businessId }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessages(prev => [...prev, { role: "assistant", content: `Done! ${data.type === "rule" ? "Categorization rule created." : data.type === "transaction" ? "Transaction added." : "Action completed."}` }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Failed to execute action." }]);
    }
  };

  const renderContent = (text) => {
    // Remove ACTION blocks from display
    const clean = text.replace(/```ACTION[\s\S]*?```/g, "").trim();
    return clean.split("\n").map((line, i) => {
      if (line.startsWith("###")) return <p key={i} className="font-semibold text-[#1e3a5f] mt-2 mb-1 text-sm">{line.replace(/^#+\s*/, "")}</p>;
      if (line.startsWith("- ") || line.startsWith("* ")) return <p key={i} className="text-xs text-[#5a6577] ml-3 mb-0.5">&bull; {line.slice(2)}</p>;
      if (line.startsWith("**") && line.endsWith("**")) return <p key={i} className="font-semibold text-[#1e3a5f] text-xs mt-1">{line.replace(/\*\*/g, "")}</p>;
      if (!line.trim()) return <br key={i} />;
      return <p key={i} className="text-xs text-[#3d4f63] mb-1 leading-relaxed">{line}</p>;
    });
  };

  const actionLabel = (a) => {
    if (a.type === "create_rule") return `Create rule: "${a.pattern}" → ${a.category}`;
    if (a.type === "add_transaction") return `Add: ${a.description} ($${a.amount})`;
    if (a.type === "navigate") return `Go to ${a.page}`;
    return "Execute";
  };

  if (!token) return null;

  return (
    <>
      {/* Floating Bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-[#1e3a5f] hover:bg-[#2d5a8e] text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105 z-50"
          data-testid="chat-bubble-btn"
        >
          <MessageCircle size={22} />
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 w-96 h-[520px] bg-white rounded-2xl shadow-2xl border border-[#e4e8ee] flex flex-col z-50 overflow-hidden" data-testid="chat-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#1e3a5f] text-white">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center">
                <MessageCircle size={14} />
              </div>
              <div>
                <p className="text-sm font-semibold">Opturna AI</p>
                <p className="text-[10px] text-white/60">Financial Assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => { setOpen(false); navigate("/assistant"); }} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="chat-expand-btn" title="Open full page">
                <Maximize2 size={14} />
              </button>
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="chat-close-btn">
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <div className="w-10 h-10 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center mx-auto mb-3">
                  <MessageCircle size={18} className="text-[#1e3a5f]" />
                </div>
                <p className="text-sm font-medium text-[#1e3a5f]">How can I help?</p>
                <p className="text-[11px] text-[#5a6577] mt-1">Ask about taxes, bookkeeping, or your finances</p>
                <div className="mt-4 space-y-2">
                  {["How is my business doing?", "What deductions am I missing?", "What should I ask my CPA?"].map((q) => (
                    <button key={q} onClick={() => { setInput(q); }} className="block w-full text-left text-xs px-3 py-2 rounded-lg bg-[#f5f7fa] text-[#1e3a5f] hover:bg-[#edf0f5] transition-colors border border-[#e4e8ee]">
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-xl px-3 py-2 ${msg.role === "user" ? "bg-[#1e3a5f] text-white" : "bg-[#f5f7fa] border border-[#e4e8ee]"}`}>
                  {msg.role === "user" ? (
                    <p className="text-xs leading-relaxed">{msg.content}</p>
                  ) : (
                    <>
                      {renderContent(msg.content)}
                      {msg.actions?.length > 0 && (
                        <div className="mt-2 space-y-1.5 border-t border-[#e4e8ee] pt-2">
                          {msg.actions.map((a, j) => (
                            <button key={j} onClick={() => executeAction(a)} className="flex items-center gap-1.5 w-full text-left text-[11px] px-2.5 py-1.5 rounded-lg bg-[#1e3a5f] text-white hover:bg-[#2d5a8e] transition-colors" data-testid={`action-btn-${i}-${j}`}>
                              <ChevronRight size={12} />
                              {actionLabel(a)}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#f5f7fa] border border-[#e4e8ee] rounded-xl px-3 py-2 flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin text-[#1e3a5f]" />
                  <span className="text-xs text-[#5a6577]">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-3 border-t border-[#e4e8ee]">
            <div className="flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                placeholder="Ask anything..."
                className="flex-1 text-sm px-3 py-2 rounded-lg bg-[#f5f7fa] border border-[#e4e8ee] text-[#1e3a5f] placeholder-[#8b95a5] focus:outline-none focus:ring-1 focus:ring-[#1e3a5f]/20"
                data-testid="chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="p-2 rounded-lg bg-[#1e3a5f] text-white hover:bg-[#2d5a8e] transition-colors disabled:opacity-40"
                data-testid="chat-send-btn"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBubble;
